import type { SpecType } from "../../../service/SpecsRevisionService";
import { SpecsDecorationsModel } from "./SpecsDecorationsModel";
import { SpecsFamiliesModel } from "./SpecsFamiliesModel";
import { SpecsFabricsModel } from "./SpecsFabricsModel";
import type { SpecsCatalog } from "./types";
import { cloneValue } from "./utils";

export type SpecsEntityType = Exclude<SpecType, "product">;
export type SpecsEntityModel =
  | SpecsFamiliesModel
  | SpecsFabricsModel
  | SpecsDecorationsModel;

export function getSpecsEntityId(
  specType: SpecsEntityType,
  entity: Record<string, unknown>
) {
  if (specType === "decoration") {
    const resolvedId =
      typeof entity.id === "string" && entity.id.trim().length > 0
        ? entity.id
        : entity.name;
    if (typeof resolvedId !== "string" || resolvedId.trim().length === 0) {
      throw new Error("decoration is missing an id or name");
    }
    return resolvedId;
  }

  if (typeof entity.name !== "string" || entity.name.trim().length === 0) {
    throw new Error(`${specType} is missing a name`);
  }

  return entity.name;
}

export function buildSpecsEntityLabel(
  specType: SpecsEntityType,
  entity: Record<string, unknown>
) {
  const id = getSpecsEntityId(specType, entity);
  if (specType !== "decoration") {
    return id;
  }

  const name =
    typeof entity.name === "string" && entity.name.trim().length > 0
      ? entity.name
      : "";
  return name && name !== id ? `${id} (${name})` : id;
}

type CreateDefaultSpecsEntitySnapshotInput = {
  specName: string;
  displayName?: string;
};

function readNewSpecName(specType: SpecsEntityType, specName: string) {
  const trimmedSpecName = specName.trim();
  if (!trimmedSpecName) {
    throw new Error(`${specType} requires a name`);
  }

  return trimmedSpecName;
}

export function createDefaultFamilySnapshot(
  input: CreateDefaultSpecsEntitySnapshotInput
) {
  const specName = readNewSpecName("family", input.specName);
  const model = new SpecsFamiliesModel();
  model.family.name = specName;

  return model.save();
}

export function createDefaultFabricSnapshot(
  input: CreateDefaultSpecsEntitySnapshotInput
) {
  const specName = readNewSpecName("fabric", input.specName);
  const model = new SpecsFabricsModel();
  model.fabric.name = specName;

  return model.save();
}

export function createDefaultDecorationSnapshot(
  input: CreateDefaultSpecsEntitySnapshotInput
) {
  const specName = readNewSpecName("decoration", input.specName);
  const resolvedDisplayName = input.displayName?.trim() || specName;
  const model = new SpecsDecorationsModel();

  model.decoration.id = specName;
  model.decoration.name = resolvedDisplayName;
  if (model.decoration.displayName) {
    model.decoration.displayName.fr = resolvedDisplayName;
    model.decoration.displayName.en = resolvedDisplayName;
  }
  model.decoration.behavesLike = specName;
  if (model.decoration.techpack) {
    model.decoration.techpack.decorationTypePrefix = specName;
    model.decoration.techpack.decorationNameKey = specName;
  }

  return model.save();
}

const createDefaultSnapshotBySpecType = {
  family: createDefaultFamilySnapshot,
  fabric: createDefaultFabricSnapshot,
  decoration: createDefaultDecorationSnapshot,
} satisfies Record<
  SpecsEntityType,
  (input: CreateDefaultSpecsEntitySnapshotInput) => Record<string, unknown>
>;

export function createDefaultSpecsEntitySnapshot(
  specType: SpecsEntityType,
  input: CreateDefaultSpecsEntitySnapshotInput
) {
  return createDefaultSnapshotBySpecType[specType](input);
}

export function isSpecsEntityIdAvailable(
  specType: SpecsEntityType,
  catalog: SpecsCatalog,
  specName: string
) {
  const catalogBySpecType = {
    family: catalog.families,
    fabric: catalog.fabrics,
    decoration: catalog.decorations,
  } satisfies Record<SpecsEntityType, unknown>;
  const collection = catalogBySpecType[specType];

  if (!Array.isArray(collection)) {
    return true;
  }

  return !collection.some(
    (entity) => getSpecsEntityId(specType, entity) === specName
  );
}

export function createSpecsModelFromCatalog(
  specType: SpecsEntityType,
  catalog: SpecsCatalog,
  specName: string
) {
  if (specType === "family") {
    return SpecsFamiliesModel.fromCatalog(catalog, specName);
  }
  if (specType === "fabric") {
    return SpecsFabricsModel.fromCatalog(catalog, specName);
  }
  return SpecsDecorationsModel.fromCatalog(catalog, specName);
}

export function restoreSpecsModel(
  specType: SpecsEntityType,
  snapshot: Record<string, unknown>
) {
  if (specType === "family") {
    return new SpecsFamiliesModel().restore(snapshot as any);
  }
  if (specType === "fabric") {
    return new SpecsFabricsModel().restore(snapshot as any);
  }
  return new SpecsDecorationsModel().restore(snapshot as any);
}

export function getSpecsValidationContext(
  specType: SpecsEntityType,
  catalog: SpecsCatalog
) {
  if (specType === "family") {
    return {
      products: Array.isArray(catalog.products) ? catalog.products : [],
    };
  }

  return {};
}

export function upsertSpecsSnapshotIntoCatalog(
  catalog: SpecsCatalog,
  specType: SpecsEntityType,
  snapshot: Record<string, unknown>
): SpecsCatalog {
  const nextCatalog = {
    ...cloneValue(catalog),
    products: Array.isArray(catalog.products)
      ? cloneValue(catalog.products)
      : [],
    families: Array.isArray(catalog.families)
      ? cloneValue(catalog.families)
      : [],
    fabrics: Array.isArray(catalog.fabrics) ? cloneValue(catalog.fabrics) : [],
    decorations: Array.isArray(catalog.decorations)
      ? cloneValue(catalog.decorations)
      : [],
  };
  const specId = getSpecsEntityId(specType, snapshot);

  const replace = (collection: Record<string, unknown>[]) => {
    const index = collection.findIndex(
      (candidate) => getSpecsEntityId(specType, candidate) === specId
    );

    if (index >= 0) {
      collection[index] = snapshot;
    } else {
      collection.push(snapshot);
    }

    return collection;
  };

  if (specType === "family") {
    nextCatalog.families = replace(
      nextCatalog.families as Record<string, unknown>[]
    ) as any;
    return nextCatalog;
  }
  if (specType === "fabric") {
    nextCatalog.fabrics = replace(
      nextCatalog.fabrics as Record<string, unknown>[]
    ) as any;
    return nextCatalog;
  }

  nextCatalog.decorations = replace(
    nextCatalog.decorations as Record<string, unknown>[]
  ) as any;
  return nextCatalog;
}
